import { useState } from "react";
import { BookText, Download, Copy, Terminal, Boxes, Braces, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API } from "@/lib/api";
import SdkPublishCard from "@/components/SdkPublishCard";
import IntegrationGuide from "@/components/IntegrationGuide";

const REST = `# Base URL
https://musicflowstudioai.com/api

# Authenticate with your API key (Authorization header)
Authorization: Bearer mf_live_xxx

# --- Generate a track ---
POST /generations
{
  "prompt": "dark cyberpunk boss battle",
  "genre": "Electronic",
  "mood": "Dark",
  "duration": 60,
  "loop": true,
  "stems": false
}
-> 200 { "id": "...", "status": "processing", "credits_used": 2 }

# --- Poll until ready ---
GET /generations/{id}
-> { "status": "completed",
     "audio_files": { "mp3": "...", "wav": "...", "loop": "..." },
     "metadata": { "bpm": 128, "key": "A minor", "tags": ["electronic","dark","boss"] },
     "license": { "id": "LIC-...", "status": "ACTIVE" } }

# --- Adaptive game music ---
POST /adaptive/session        { "game_name": "Neon Drift", "mood": "dark" }
POST /adaptive/intensity      { "session_id": "...", "intensity": 0.8 }
POST /adaptive/transition     { "session_id": "...", "state": "boss" }
-> { "params": { "bpm": 160, "layers": ["ambient_pad","bass","drums","lead_synth"] } }

# --- PlayerPulse → FeedbackLoop → MetaCompoder (every 1-3 s) ---
POST /adaptive/environment    { "session_id": "...", "environment": "cave" }   # AmbientCrafter → params.mix {reverb, ducking, compressor, limiter} (null clears)
POST /adaptive/pulse          { "session_id": "...", "hp": 0.4, "damage_taken": 0.3, "enemies_nearby": 4, "deaths": 1 }
-> { "tension": 0.62, "intensity": 0.55, "applied": true, "decision": { "reason": "pulse: damage 30%, hp 40%, 4 enemies" } }
POST /adaptive/narrative-cue  { "session_id": "...", "state": "boss", "hold_sec": 30 }   # locks pulses out for 30 s
POST /adaptive/narrative-cue  { "session_id": "...", "theme_id": "...", "event": "enter_arena" }   # Cue sheet zone → state + environment (reverb) from the sheet
GET  /adaptive/decisions?session_id=...                                                 # decision log

# --- VoiceManager runtime (cached voice lines, FX, culture, music ducking) ---
POST /voice/speak             { "text": "Attention, le dragon arrive !", "character": "warrior", "emotion": "peur", "effect": "robot", "session_id": "..." }
-> { "audio_url": "/api/audio/...", "cached": false, "seconds": 1.7, "credits_charged": 2, "params": { "master_volume": 0.18, "ducked": true } }
   + "lipsync": { "words": [{ "word": "Attention,", "start": 0.0, "end": 0.61 }], "visemes": [{ "v": "aa", "start": 0.0, "end": 0.12 }] }
   emotion blending: add "emotion2": "colère", "blend": 0.4   (40 % of the second emotion)
GET  /voice/runtime/effects   # robot, monster, giant, ghost, radio, megaphone, cave, chipmunk, whisper, demon, telephone + cultures
GET  /voice/speak/pack?character=warrior   # Runtime Voice Pack ZIP: mp3 per line + manifest.json (text, emotions, FX, lipsync) for offline shipping
GET  /voice/speak/pack/manifest            # JSON manifest with download URLs — SDK SyncVoicePack() fetches it at startup (offline fallback)
GET  /voice/dialogue/{id}/lipsync          # cutscene mouth timing: per-line words/visemes + global timeline (offsets = SRT / scene MP3 layout)
GET  /voice/dialogue/{id}/lipsync-bundle   # ZIP: per-line audio + lipsync.json + script
POST /voice/dialogue/{id}/precache         # Dialogue Auto Speak: push every line into the runtime cache (same voice/emotion) → Speak() / Voice Pack
GET  /audio/{path}?format=wav              # on-the-fly WAV transcode (Unreal streams PCM WAV)
POST /adaptive/voice          { "session_id": "...", "speaking": true, "level": 0.35, "seconds": 6 }   # DynamicMixer ducking

# --- Themes & leitmotifs / NarrativeSync ---
POST /narrative/analyze       { "script": "ACT 1 — ...", "provider": "claude" }        # scenes → emotions → plan (free)
POST /narrative/{id}/build                                                              # theme + variations + cue sheet
GET  /themes/{id}/manifest    # one audio URL per state, shared key/BPM
GET  /themes/{id}/cuesheet    # events → state, at_sec, fade_ms, stingers
GET  /themes/{id}/export/fmod | /export/wwise | /midi   # ?env=cave → native reverb + compressor + limiter chain (mixbus.json, FMOD script, Wwise WAAPI script)

# --- Mix Bus (ffmpeg, free) — equivalents of TAL-Reverb / Melda / Blue Cat / LoudMax ---
GET  /mixbus/options                       # spaces, EQ bands, modulations, presets, defaults
POST /generations/{id}/mixbus { "preset": "dungeon_cave" }                            # → new track kind "mix" + QA (spectrum)
POST /generations/{id}/mixbus { "chain": { "strip": { "eq": [{"f":2500,"g":2}], "comp": {"threshold_db":-18,"ratio":3} },
                                            "reverb": { "space": "cave", "mix": 0.3 }, "sidechain": { "voice_id": "...", "mix_voice": true },
                                            "modulation": { "type": "chorus" }, "limiter": { "ceiling_db": -1, "lufs": -16 } } }
POST /voice/{id}/mixbus       { "preset": "broadcast_voice" }                          # voice / SFX → new creation
GET|POST /mixbus/presets      { "name": "Mon donjon", "chain": {...} }                  # personal presets (usable as "preset": "<id|name>") · DELETE /mixbus/presets/{id}
POST /generations/{id}/mixbus/preview { "preset": "cathedral", "seconds": 30 }        # ephemeral mp3 (compare presets, nothing saved)
GET  /themes/{id}/cuesheet/render?fmt=mp3&reverb=true                                  # one file: states looped + cross-faded + reverb zones (scene mock-up)
POST /themes/{id}/cues/suggest-zones { "apply": false, "overwrite": false, "ai": true } # reverb zone per cue (keywords; ai=true → Claude reads the scenario)
POST /themes/{id}/cuesheet/share { "hours": 72, "note": "..." }                         # public temporary link → /share/{token} (+ GET /share/{token}, /share/{token}/audio?format=wav, no auth)
GET|POST /share/{token}/comments { "author": "Client", "time": 42.5, "text": "...", "email": "client@x.com" }  # client notes (public; email optional → notified on reply) · owner: GET /themes/{id}/cuesheet/comments, PATCH …/comments/{cid} { "resolved": true, "reply": "..." }, DELETE, GET …/cuesheet/comments.pdf?token=
POST /themes/{id}/cuesheet/share/{token}/email { "to": "client@x.com", "message": "...", "attach_pdf": true }  # validation email: public link + notes PDF
POST /themes/{id}/cuesheet/share/{token}/next  { "hours": 72 }                          # V2 of a mock-up after corrections → public page lists the previous version's resolved notes (GET /share/{token} → previous/next) + synced A/B V1/V2 player
POST /share/{token}/approve { "author": "Nom complet", "email": "...", "message": "...", "accept": true }  # public: signed validation (typed name + acceptance box → SHA-256 signature id, GET /share/{token}/certificate.pdf, in the bundle) (once per link) → Slack + email to the team · GET /themes/{id}/cuesheet/shares → version history (plays, comments, sent_to, reminder, approval)
GET  /share/{token}/bundle.zip                                                          # public, approved mock-ups only: MP3 + cuesheet.json + notes PDF + README
POST /themes/{id}/cuesheet/share/{token}/remind · GET /cuesheet/share-stats              # manual reminder · Billing analytics (plays, approval rate, turnaround)
GET|PATCH /cuesheet/reminder-settings { "days": 1|3|7, "second": true, "enabled": true }   # automatic reminder delay (+ optional second reminder) for all your shared mock-ups
POST /generations { …, "engine": "premium" }                                          # premium engine (ElevenLabs Eleven Music, Suno adapter when official) · monthly premium credits per plan (1 cr = 30 s, +1 stems) · automatic internal fallback · GET /premium/status, /premium/usage, /premium/estimate
POST /portal/request { "email", "lang" } · GET /portal/{token}                         # client portal (FR/EN: ?lang=, auto from browser / client_lang; approve/comments/email accept "lang"): magic link (7 days) listing every mock-up shared with that address (versions, status, notes, certificate, bundle)
# SDK: music.LoadSharedCueSheet(link, source) (Unity) · Music->LoadSharedCueSheet(Link) (Unreal) · MusicFlow.play_shared_cue_sheet(link, $Player) (Godot) · mf.playSharedCueSheet(link, {onCue}) (npm) → mock-up + a cue callback each cue, no API key
POST /themes/{id}/mixbus      { "preset": "master_game", "apply": true }                # same chain on reference + all variations (homogeneous) · POST /themes/{id}/mixbus/revert`;

const JS = `import MusicFlow from "@musicflow/sdk";

const mf = new MusicFlow("mf_live_xxx");

// Generate and wait for completion
const track = await mf.generate({
  prompt: "epic orchestral battle theme",
  genre: "Orchestral",
  mood: "Epic",
  duration: 60,
  loop: true,
});

console.log(track.audio_files.mp3);   // download URL
console.log(track.metadata.bpm);      // 140
console.log(track.metadata.key);      // "D minor"

// Adaptive session
const session = await mf.adaptive.createSession({ game: "Neon Drift" });
session.setIntensity(0.8);
session.transition("boss");`;

const UNITY = `using System.Collections;
using UnityEngine;
using UnityEngine.Networking;

// MusicFlow adaptive soundtrack controller for Unity
public class MusicFlowController : MonoBehaviour
{
    public string apiKey = "mf_live_xxx";
    private string sessionId;
    private const string BASE = "https://musicflowstudioai.com/api";

    IEnumerator Start()
    {
        yield return Post("/adaptive/session",
            "{\\"game_name\\":\\"Neon Drift\\",\\"mood\\":\\"dark\\"}",
            res => sessionId = ParseSessionId(res));
    }

    // Call from gameplay code as the action heats up
    public void SetIntensity(float value)
    {
        string body = "{\\"session_id\\":\\"" + sessionId + "\\",\\"intensity\\":" + value + "}";
        StartCoroutine(Post("/adaptive/intensity", body, null));
    }

    public void Transition(string state) // "exploration" | "combat" | "boss"
    {
        string body = "{\\"session_id\\":\\"" + sessionId + "\\",\\"state\\":\\"" + state + "\\"}";
        StartCoroutine(Post("/adaptive/transition", body, null));
    }

    IEnumerator Post(string path, string json, System.Action<string> onDone)
    {
        var req = new UnityWebRequest(BASE + path, "POST");
        req.uploadHandler = new UploadHandlerRaw(System.Text.Encoding.UTF8.GetBytes(json));
        req.downloadHandler = new DownloadHandlerBuffer();
        req.SetRequestHeader("Content-Type", "application/json");
        req.SetRequestHeader("Authorization", "Bearer " + apiKey);
        yield return req.SendWebRequest();
        onDone?.Invoke(req.downloadHandler.text);
    }

    string ParseSessionId(string res) => JsonUtility.FromJson<SessionResp>(res).session_id;
    [System.Serializable] class SessionResp { public string session_id; }
}`;

const UNREAL = `// Unreal Engine 5.3+ — install the MusicFlow plugin (download below), then from C++ or Blueprint:
#include "MusicFlowSubsystem.h"

void AMyGameMode::BeginPlay()
{
    Super::BeginPlay();
    auto* Music = GetGameInstance()->GetSubsystem<UMusicFlowSubsystem>();
    Music->Configure(TEXT("mf_live_..."));                       // API Keys page
    Music->OnTrackReady.AddDynamic(this, &AMyGameMode::OnTrackReady);

    // 1) Generate an original loop and stream it in-game (WAV → SoundWave, cross-fade)
    Music->Generate(TEXT("dark synthwave chase loop"), TEXT("Synthwave"), TEXT("Dark"),
                    /*Duration*/ 30, /*bLoop*/ true, /*bAutoPlay*/ true);

    // 2) Adaptive score driven by gameplay
    Music->CreateSession(TEXT("Neon Drift"), TEXT("dark"));
    Music->Transition(TEXT("boss"));
    Music->SetIntensity(1.0f);
    Music->TriggerCue(TEXT("Scene 1"));                          // cutscene cue → plays its audio
    Music->ReportPulse(Hp / MaxHp, Damage / MaxHp, Enemies.Num(), Deaths, Kills); // PlayerPulse every 1-3 s
    Music->NarrativeCue(TEXT("boss"), 30.f);                     // boss intro: state locked 30 s
    Music->Speak(TEXT("Attention, le dragon arrive !"), TEXT("warrior"), TEXT("peur"), TEXT("robot")); // cached voice line, music ducked
}

void AMyGameMode::OnTrackReady(const FString& GenerationId, const FString& AudioPath)
{
    UE_LOG(LogTemp, Log, TEXT("MusicFlow track %s ready: %s"), *GenerationId, *AudioPath);
}

// Blueprint: "Get MusicFlow" → Configure / Generate / PlayTrack / CreateSession / SetIntensity / Transition / TriggerCue / ReportPulse / NarrativeCue
// Events: OnTrackReady · OnPlaybackStarted · OnSessionReady · OnAdaptiveParams · OnError`;

const TABS = [
  { key: "rest", label: "REST API", icon: Terminal, code: REST, lang: "http", file: "musicflow-api-reference.md" },
  { key: "js", label: "JavaScript", icon: Braces, code: JS, lang: "js", file: "musicflow.js" },
  { key: "unity", label: "Unity (C#)", icon: Boxes, code: UNITY, lang: "csharp", file: "MusicFlowController.cs" },
  { key: "unreal", label: "Unreal (C++)", icon: Boxes, code: UNREAL, lang: "cpp", file: "MusicFlowSubsystem.cpp" },
];

function downloadFile(filename, content) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Docs() {
  const [tab, setTab] = useState("rest");
  const [sdkLoading, setSdkLoading] = useState(null);

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copied."); }
    catch { toast.error("Copy failed — select manually."); }
  };

  const downloadSdkPackage = async (engine) => {
    setSdkLoading(engine);
    const names = { unity: "Unity", unreal: "Unreal", "unreal-demo": "Unreal demo project", godot: "Godot", npm: "npm", python: "Python" };
    const files = { npm: "musicflow-sdk.tgz", python: "musicflow-studioai.tar.gz", "unreal-demo": "musicflow-unreal-demo.zip" };
    const file = files[engine] || `musicflow-${engine}-sdk.zip`;
    try {
      const res = await fetch(`${API}/sdk/${engine}`, { credentials: "include" });
      if (!res.ok) throw new Error("sdk failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${names[engine]} SDK package downloaded.`);
    } catch {
      toast.error("Could not download the SDK package.");
    } finally { setSdkLoading(null); }
  };

  const downloadFullKit = () => {
    const kit = TABS.map((t) => `===== ${t.file} =====\n\n${t.code}`).join("\n\n\n");
    downloadFile("musicflow-sdk-kit.txt", kit);
    toast.success("SDK kit downloaded.");
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
            <BookText className="text-primary" /> SDK & API Docs
          </h1>
          <p className="text-muted-foreground mt-1">Integrate MusicFlow into your backend, web app, or game engine.</p>
        </div>
        <Button onClick={downloadFullKit} className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary" data-testid="download-sdk-kit">
          <Download size={16} className="mr-1.5" /> Download SDK Kit
        </Button>
      </header>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <InfoCard title="Base URL" value="musicflowstudioai.com/api" />
        <InfoCard title="Auth" value="Bearer mf_live_…" />
        <InfoCard title="Format" value="JSON over HTTPS" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <SdkCard engine="unity" title="Unity Package" btn="Unity package" desc="Drop-in MonoBehaviour + asmdef + package.json, plus MusicFlow.Dsp: native Mix Bus components (Reverb spaces, Channel Strip, true-peak Limiter via OnAudioFilterRead — no VST)." loading={sdkLoading === "unity"} onDownload={() => downloadSdkPackage("unity")} />
        <SdkCard engine="unreal" title="Unreal Plugin (UE 5.3–5.5)" btn="Unreal plugin" desc="Complete plugin: Blueprint nodes (Generate, PlayTrack, CreateSession, SetIntensity, Transition, TriggerCue, ReportPulse, NarrativeCue), in-game WAV streaming with cross-fade & gap-less loop, OnTrackReady events, Mix Bus library (ApplyEnvironment / ApplyCompressor / ApplyEq / ApplyLimiter on submixes)." loading={sdkLoading === "unreal"} onDownload={() => downloadSdkPackage("unreal")} />
        <SdkCard engine="unreal-demo" title="Unreal Demo Project" btn="Demo project" desc="Ready-to-launch UE 5.x sample: level + GameMode driving an adaptive score from the keyboard (G generate, 1-4 states, arrows intensity, C cue). Plugin bundled." loading={sdkLoading === "unreal-demo"} onDownload={() => downloadSdkPackage("unreal-demo")} />
        <SdkCard engine="godot" title="Godot 4 Add-on" btn="Godot add-on" desc="GDScript autoload for Godot 4. set_intensity / transition / set_mood + generate() with a track_ready signal." loading={sdkLoading === "godot"} onDownload={() => downloadSdkPackage("godot")} />
        <SdkCard engine="npm" title="JavaScript / npm" btn="npm tarball" desc="Installable @musicflow/sdk package with TypeScript types. Run npm install ./musicflow-sdk.tgz." loading={sdkLoading === "npm"} onDownload={() => downloadSdkPackage("npm")} />
        <SdkCard engine="python" title="Python" btn="Python package" desc="Published on PyPI as musicflow-studioai (pip install musicflow-studioai). Download the source package (pyproject, LICENSE, types) or publish a new version below." loading={sdkLoading === "python"} onDownload={() => downloadSdkPackage("python")} />
      </div>
      <div className="mb-8"><SdkPublishCard /></div>
      <div className="mb-10 rounded-2xl border border-border bg-card/40 p-5 lg:p-6"><IntegrationGuide /></div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted mb-4 flex-wrap h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`docs-tab-${t.key}`} className="gap-1.5">
              <t.icon size={14} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="m-0">
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-background/60">
                <span className="font-mono text-xs text-muted-foreground">{t.file}</span>
                <div className="flex gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => copy(t.code)} className="text-muted-foreground hover:text-primary h-7" data-testid={`copy-${t.key}`}>
                    <Copy size={13} className="mr-1" /> Copy
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { downloadFile(t.file, t.code); toast.success("Downloaded."); }} className="text-muted-foreground hover:text-primary h-7" data-testid={`download-${t.key}`}>
                    <Download size={13} className="mr-1" /> Download
                  </Button>
                </div>
              </div>
              <pre className="p-5 overflow-x-auto text-xs font-mono leading-relaxed text-foreground/90 max-h-[520px]">{t.code}</pre>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

const InfoCard = ({ title, value }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <div className="text-xs text-muted-foreground">{title}</div>
    <div className="font-mono font-bold mt-1 text-primary break-all">{value}</div>
  </div>
);

const SdkCard = ({ engine, title, desc, btn, loading, onDownload }) => (
  <div className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-5 flex flex-col" data-testid={`sdk-card-${engine}`}>
    <div className="flex items-center gap-2 mb-1">
      <Package size={18} className="text-primary" />
      <div className="font-display text-lg font-bold">{title}</div>
    </div>
    <p className="text-sm text-muted-foreground flex-1">{desc}</p>
    <Button onClick={onDownload} disabled={loading} className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90 self-start" data-testid={`download-sdk-${engine}`}>
      {loading ? <Loader2 className="animate-spin mr-1.5" size={16} /> : <Download size={16} className="mr-1.5" />} Download {btn}
    </Button>
  </div>
);
